import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/**
 * HydroWata cart behaviour (agent F, 11/10/2026) for snippets/hw-cart.liquid — drawer and /cart page.
 *
 * Every cart write here:
 *  - runs one at a time (a queue: two writes in flight = last write wins on the whole cart);
 *  - asks for the HTML of every cart-items-component section on the page (drawer + /cart page);
 *  - dispatches ONE CartLinesUpdateEvent (@shopify/events) whose promise resolves with that HTML, so Horizon's
 *    cart-items-component morphs the drawer, the header bubble updates and any other listener (gift guards) sees it;
 *  - addresses lines by line KEY (never by index — hidden lines shift indexes).
 * Controls (delegated on document, registered once — the markup is morphed): stepper [data-hw-step], trash
 * [data-hw-remove], colour select [data-hw-variant], upsell [data-hw-up], Shipping Protection [data-hw-sp-toggle],
 * Clear all [data-hw-clear] (two taps), Check out [data-hw-checkout] (waits for the queue).
 * Public: window.HWCart = { queue, post, get, announce, write, refresh, idle } — the gift guard (assets/hw-cart-guard.js)
 * and any other cart writer run through queue(); await HWCart.idle() before reading the cart for checkout.
 */
if (!window.HWCart) {
  /** @type {Promise<any>} */
  let chain = Promise.resolve();
  let pending = 0;

  const sectionIds = () => {
    const ids = new Set();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
    });
    return [...ids];
  };

  const busy = (on) => {
    document.querySelectorAll('cart-items-component').forEach((el) => el.classList.toggle('cart-items-disabled', on));
    document.documentElement.classList.toggle('hw-cart-busy', on);
  };

  /** Run `task` after every earlier cart task has settled (one cart request chain at a time). */
  const queue = (task) => {
    pending++;
    busy(true);
    const p = chain.then(task, task);
    chain = p.catch(() => {});
    p.finally(() => {
      pending--;
      if (!pending) busy(false);
    }).catch(() => {});
    return p;
  };

  /** POST a cart endpoint; `withSections` asks for the HTML of every cart-items-component section. */
  const post = async (url, payload, withSections = true) => {
    const extra = withSections ? { sections: sectionIds().join(','), sections_url: window.location.pathname } : {};
    const res = await fetch(url, fetchConfig('json', { body: JSON.stringify({ ...payload, ...extra }) }));
    const data = await res.json();
    if (!res.ok || data.status || (data.errors && !data.items)) {
      const msg = data.description || data.message || data.errors || 'Cart update failed';
      throw Object.assign(new Error(String(msg)), { cartError: true, data });
    }
    return data;
  };

  const get = async () => {
    const res = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    return res.json();
  };

  const detailOf = (data, source) => ({
    sections: data.sections || undefined,
    items: data.items,
    itemCount: data.item_count,
    source,
    didError: false,
  });

  /** Tell the theme about a cart that already changed (`data` = full cart JSON + sections). */
  const announce = (data, meta = {}) => {
    const deferred = CartLinesUpdateEvent.createPromise();
    const from = (meta.from && meta.from.isConnected && meta.from) || document.querySelector('[data-hw-cart]') || document;
    from.dispatchEvent(
      new CartLinesUpdateEvent({ action: meta.action || 'update', context: 'cart', lines: meta.lines || [], promise: deferred.promise })
    );
    deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data), detail: detailOf(data, meta.source || 'hw-cart') });
  };

  /**
   * One cart write: event first (Horizon shows its loading state), then the request, then the event resolves with
   * the fresh sections → the drawer / cart page morph. `url` must return the full cart (update / change / clear).
   * @param {string} url
   * @param {object} payload
   * @param {{ action: 'add'|'update'|'remove', lines: any[], from?: Element | null }} meta
   */
  const write = (url, payload, meta) => job(meta, () => post(url, payload, true));

  /**
   * A queued cart job: event first, then `task` (one or more requests; it must return the full cart JSON of the
   * last one WITH sections), then the event resolves → one repaint.
   * @param {{ action: 'add'|'update'|'remove', lines: any[], from?: Element | null }} meta
   * @param {() => Promise<any>} task
   */
  function job(meta, task) {
    return queue(async () => {
      const deferred = CartLinesUpdateEvent.createPromise();
      const from = (meta.from && meta.from.isConnected && meta.from) || document.querySelector('[data-hw-cart]') || document;
      from.dispatchEvent(
        new CartLinesUpdateEvent({ action: meta.action, context: 'cart', lines: meta.lines, promise: deferred.promise })
      );
      try {
        const data = await task();
        deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data), detail: detailOf(data, 'hw-cart') });
        // a Shipping Protection line with nothing left to protect goes too (queued right behind this job)
        const items = Array.isArray(data.items) ? data.items : [];
        const sp = items.filter((i) => i.handle === 'shipping-protection');
        if (sp.length && sp.length === items.length) {
          write(Theme.routes.cart_update_url, { updates: Object.fromEntries(sp.map((i) => [i.key, 0])) }, { action: 'remove', lines: [] }).catch(() => {});
        }
        return data;
      } catch (error) {
        deferred.reject(error);
        console.warn('[hw-cart]', error);
        // repaint from the server so the drawer never shows a state the cart does not have
        refresh();
        throw error;
      }
    });
  }

  /** Re-render the cart sections from the server (no cart change). */
  const refresh = () =>
    queue(async () => {
      const data = await post(Theme.routes.cart_update_url, { updates: {} }, true);
      announce(data, { source: 'hw-cart-refresh' });
      return data;
    }).catch(() => {});

  /** Resolves when every queued cart task (incl. the gift guard's) has settled. */
  const idle = async () => {
    await chain;
    const g = window.HWGiftGuard;
    if (g && typeof g.idle === 'function') await g.idle();
    await chain;
  };

  window.HWCart = { queue, post, get, announce, write, refresh, idle, pending: () => pending };

  /** Lines behind one row: [{ key, qty, holds }] — holds = the line carries nested gift lines. */
  const linesOf = (row) =>
    (row.getAttribute('data-keys') || '')
      .split(' ')
      .filter(Boolean)
      .map((x) => {
        const [key, qty, holds] = x.split('~');
        return { key, qty: parseInt(qty, 10) || 0, holds: holds === '1' };
      });

  /** Rows of `root` showing variant `vid`, other than `row`. */
  const otherRows = (row, vid) => {
    const root = row.closest('[data-hw-cart]') || document;
    return [...root.querySelectorAll('.cd__ln[data-variant-id]')].filter((r) => r !== row && r.getAttribute('data-variant-id') === vid);
  };

  /**
   * Bring a row to `target` units (+ gift trims), one queued job, one repaint.
   * Shopify splits ONE variant into several lines when discounts allocate differently (2 bottles = a "FREE TABLETS"
   * buy line + a "First-Order Deal" line). Measured 11/10/2026 on the draft:
   *  - /cart/change.js {id: either portion key, quantity: q} sets THAT portion (1 + 1: first → 2 = 3, second → 2 = 3,
   *    first → 0 = 1, second → 0 = 1; nested gift kept) ✓ (f/exp2.js);
   *  - /cart/update.js keyed by the VARIANT id sets only the first portion (1 + 1, {vid: 3} → 4 bottles) ✗.
   * So: change.js on the first portion (= target − the other portions); when the target is below the other portions
   * (trash on a split row), zero portion by portion from the freshest cart. Gift trims follow in one update.js keyed by
   * the gift variant (gift variants live on one line each), which also carries the sections for the repaint.
   */
  const setRow = (row, target, from) => {
    const vid = row.getAttribute('data-variant-id') || '';
    const parent = row.getAttribute('data-parent-key') || '';
    const lines = linesOf(row);
    const trims = row.hasAttribute('data-hw-gift') ? {} : giftTrims(row, target);
    const hasTrims = Object.keys(trims).length > 0;
    const rest = lines.slice(1).reduce((a, l) => a + l.qty, 0);
    return job({ action: target === 0 ? 'remove' : 'update', lines: lines.map((l) => ({ id: l.key, quantity: target })), from }, async () => {
      let data;
      if (lines.length <= 1 || target >= rest) {
        const key = lines.length ? lines[0].key : row.getAttribute('data-key');
        data = await post(Theme.routes.cart_change_url, { id: key, quantity: lines.length ? target - rest : target }, !hasTrims);
      } else {
        data = await get();
        for (let i = 0; i < 6; i++) {
          const mine = (data.items || []).filter(
            (it) => String(it.variant_id) === vid && ((it.parent_relationship && it.parent_relationship.parent_key) || '') === parent
          );
          const total = mine.reduce((a, it) => a + it.quantity, 0);
          if (!mine.length || total <= target) break;
          const l = mine[0];
          data = await post(Theme.routes.cart_change_url, { id: l.key, quantity: Math.max(0, target - (total - l.quantity)) }, false);
        }
        if (!hasTrims) data = await post(Theme.routes.cart_update_url, { updates: {} }, true);
      }
      if (hasTrims) data = await post(Theme.routes.cart_update_url, { updates: trims }, true);
      return data;
    });
  };

  const rowOf = (el) => (el instanceof Element ? el.closest('.cd__ln[data-key]') : null);

  /** When the bottle count changes, trim gift rows above the new entitlement IN THE SAME request (the gift guard
   *  would do it one request later, but the BXGY tablets would show as charged $44.99 in between). */
  const giftTrims = (row, target) => {
    const G = window.HWGiftGuard;
    const rules = G && G.rules ? G.rules() : null;
    if (!rules || !rules.deal || row.dataset.product !== rules.main.handle) return {};
    const root = row.closest('[data-hw-cart]');
    if (!root) return {};
    let bottles = target;
    root.querySelectorAll('.cd__ln[data-key]:not([data-hw-gift])').forEach((r) => {
      if (r !== row && r instanceof HTMLElement && r.dataset.product === rules.main.handle) bottles += parseInt(r.dataset.qty || '0', 10) || 0;
    });
    const earn = G.entitled(rules, bottles);
    const out = {};
    /** @type {Record<string, number>} */
    const held = {};
    root.querySelectorAll('.cd__ln[data-hw-gift]').forEach((g) => {
      if (!(g instanceof HTMLElement)) return;
      const vid = g.dataset.variantId || '';
      if (!rules.gifts.some((x) => String(x.variant) === vid)) return;
      held[vid] = (held[vid] || 0) + (parseInt(g.dataset.qty || '0', 10) || 0);
    });
    // keyed by the gift variant (only gift rows carry it): the variant's total drops to what is still earned
    for (const [vid, n] of Object.entries(held)) {
      const allow = earn[vid] ? earn[vid].qty : 0;
      if (n > allow) out[vid] = allow;
    }
    return out;
  };

  // ---- stepper: show the new number at once, send one request after a short pause ----
  /** @type {Map<string, number>} */
  const timers = new Map();
  const stepRow = (row, delta) => {
    const out = row.querySelector('[data-hw-qty]');
    const now = parseInt(row.getAttribute('data-want') || row.dataset.qty || '1', 10) || 0;
    const want = Math.max(0, now + delta);
    row.setAttribute('data-want', String(want));
    if (out) out.textContent = String(want);
    const key = row.dataset.key || '';
    clearTimeout(timers.get(key));
    timers.set(
      key,
      window.setTimeout(() => {
        timers.delete(key);
        const target = parseInt(row.getAttribute('data-want') || '0', 10);
        if (target === parseInt(row.dataset.qty || '0', 10)) return;
        setRow(row, target, row).catch(() => {});
      }, 350)
    );
  };

  // ---- Clear all: first tap arms ("Tap again to clear", mockup copy), second tap within 4 s empties ----
  let armed = null;
  const disarm = () => {
    if (!armed) return;
    clearTimeout(armed.timer);
    if (armed.button.isConnected) {
      armed.button.textContent = 'Clear all';
      armed.button.classList.remove('is-ask');
    }
    armed = null;
  };

  document.addEventListener('click', (event) => {
    const t = event.target instanceof Element ? event.target : null;
    if (!t) return;

    const clear = t.closest('[data-hw-clear]');
    if (!clear) disarm();

    const step = t.closest('[data-hw-step]');
    if (step) {
      event.preventDefault();
      const row = rowOf(step);
      if (row) stepRow(row, parseInt(step.getAttribute('data-hw-step') || '0', 10));
      return;
    }

    const del = t.closest('[data-hw-remove]');
    if (del) {
      event.preventDefault();
      const row = rowOf(del);
      if (!row) return;
      row.classList.add('is-removing');
      setRow(row, 0, del).catch(() => row.classList.remove('is-removing'));
      return;
    }

    const up = t.closest('[data-hw-up]');
    if (up) {
      event.preventDefault();
      const id = up.getAttribute('data-hw-up');
      if (!id) return;
      up.setAttribute('aria-busy', 'true');
      write(Theme.routes.cart_update_url, { updates: { [id]: 1 } }, {
        action: 'add',
        lines: [{ merchandiseId: id, quantity: 1 }],
        from: up,
      }).catch(() => up.removeAttribute('aria-busy'));
      return;
    }

    if (clear) {
      event.preventDefault();
      if (!armed || armed.button !== clear) {
        disarm();
        clear.textContent = 'Tap again to clear';
        clear.classList.add('is-ask');
        armed = { button: clear, timer: window.setTimeout(disarm, 4000) };
        return;
      }
      clearTimeout(armed.timer);
      armed = null;
      write(`${Theme.routes.cart_url}/clear.js`, {}, { action: 'remove', lines: [], from: clear }).catch(() => {});
      return;
    }

    const co = t.closest('[data-hw-checkout]');
    if (co && pending) {
      // hold Check out until every cart request has settled, then go
      event.preventDefault();
      idle().then(() => {
        const form = document.getElementById('hw-cart-form');
        if (form instanceof HTMLFormElement) form.requestSubmit(co instanceof HTMLButtonElement ? co : undefined);
      });
    }
  });

  document.addEventListener('change', (event) => {
    const t = event.target;
    if (t instanceof HTMLInputElement && t.matches('[data-hw-sp-toggle]')) {
      const card = t.closest('[data-hw-sp]');
      if (!card) return;
      const vid = card.getAttribute('data-hw-sp');
      const key = card.getAttribute('data-key');
      if (t.checked && vid) {
        write(Theme.routes.cart_update_url, { updates: { [vid]: 1 } }, { action: 'add', lines: [{ merchandiseId: vid, quantity: 1 }], from: t }).catch(() => {});
      } else if (!t.checked && key) {
        write(Theme.routes.cart_change_url, { id: key, quantity: 0 }, { action: 'remove', lines: [{ id: key, quantity: 0 }], from: t }).catch(() => {});
      }
      return;
    }
    // cart-like-chopchop 23/10/2026: one select per unit (ChopChop USA swapUnit) - moves ONE unit of the row to the chosen variant:
    // that line -1 (by its key), the chosen variant +1 (on top of what the cart already holds of it), one update.
    if (t instanceof HTMLSelectElement && t.matches('[data-hw-unit]')) {
      const from = t.getAttribute('data-hw-from') || '';
      const key = t.getAttribute('data-hw-key') || '';
      const to = t.value;
      if (!from || !to || from === to) return;
      job({ action: 'update', lines: [{ merchandiseId: to, quantity: 1 }], from: t }, async () => {
        const cart = await get();
        const items = Array.isArray(cart.items) ? cart.items : [];
        const top = (i) => !(i.parent_relationship && i.parent_relationship.parent_key);
        const line =
          items.find((i) => i.key === key) || items.find((i) => String(i.variant_id) === from && top(i) && i.final_line_price > 0);
        if (!line) throw new Error('line not in the cart any more');
        const target = items.find((i) => String(i.variant_id) === to && top(i));
        const updates = { [line.key]: line.quantity - 1, [to]: (target ? target.quantity : 0) + 1 };
        return post(Theme.routes.cart_update_url, { updates }, true);
      }).catch(() => {
        t.value = from;
      });
      return;
    }
    if (t instanceof HTMLSelectElement && t.matches('[data-hw-variant]')) {
      const from = t.getAttribute('data-hw-variant') || '';
      const to = t.value;
      if (!from || !to || from === to) return;
      const qty = parseInt(t.getAttribute('data-line-qty') || '1', 10) || 1;
      const already = parseInt(t.selectedOptions[0]?.getAttribute('data-cart-qty') || '0', 10) || 0;
      // Shopify cannot change a line's variant: one update — this row's line → 0 (by its key), the new variant → what
      // the cart already holds of it + this row's quantity, so an existing line is topped up, never overwritten
      const row = rowOf(t);
      const lines = row ? linesOf(row) : [];
      const updates = lines.length ? Object.fromEntries(lines.map((l) => [l.key, 0])) : { [from]: 0 };
      updates[to] = already + qty;
      write(Theme.routes.cart_update_url, { updates }, {
        action: 'update',
        lines: [{ merchandiseId: to, quantity: already + qty }],
        from: t,
      }).catch(() => {
        t.value = from;
      });
    }
  });

  // ---- after every repaint: disarm Clear all; on load: drop a protection line left alone in the cart ----
  document.addEventListener(StandardEvents.cartLinesUpdate, disarm);
  const tidy = () => {
    const root = document.querySelector('[data-hw-sp-orphan]');
    const key = root && root.getAttribute('data-hw-sp-orphan');
    if (key && !pending) {
      write(Theme.routes.cart_change_url, { id: key, quantity: 0 }, { action: 'remove', lines: [{ id: key, quantity: 0 }] }).catch(() => {});
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', tidy, { once: true });
  else tidy();
}
